// packages/storefront/src/api/publicApiRequest.ts
import { fetchApiRequest } from '@tourtask/utils/shared/fetchApiRequest';
import type { Result } from '@tourtask/types/result';
import { apiV1BaseUrl } from '@tourtask/storefront/api/baseUrls';

interface PublicApiRequestProps {
  /** Path under `/v1`, e.g. `businesses/my-biz/data?lang=en`. No leading slash. */
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * Optional body for non-GET requests. A plain object is sent as JSON; a
   * `FormData` is sent as `multipart/form-data` with the client writing the
   * boundary (the manual-payment proof upload is the only such call today —
   * see `declareManualPayment.ts`).
   */
  body?: Record<string, unknown> | FormData;
  /**
   * BizToken — sent as `Authorization: Bearer <token>` header. Required
   * for any endpoint declared with `auth: 'bizToken'` server-side
   * (effectively every per-business endpoint).
   */
  bizToken: string;
  /**
   * Per-booking access token — sent as `Booking-Token: <token>` header.
   * Required for endpoints that read or act on a specific booking
   * (`/businesses/{slug}/bookings/{bookingNumber}/...`,
   * `/payments/{gateway}/sessions` for booking payments). Distinct from
   * `bizToken`: bizToken proves "this is a legit storefront for biz X",
   * bookingToken proves "this caller may touch booking N specifically".
   *
   * Per AGENTS.md "Public API Conventions / Auth & security", capability
   * tokens travel in headers, never in path or query strings.
   */
  bookingToken?: string;
  /**
   * Cloudflare Turnstile token from the bot-challenge widget — sent as
   * `Turnstile-Token: <token>` header. Required (server-side, when
   * `TURNSTILE_SECRET_KEY` is configured on the API) on customer-facing
   * write endpoints: booking creation and payment-session creation.
   * Pass-through no-op today: no storefront code reads a Turnstile site key
   * yet (the rollout's client step in apps/api/src/lib/security/verifyTurnstile.ts
   * is not done), so neither the widget's env nor a hosted site's gains
   * anything from one.
   */
  turnstileToken?: string;
  /**
   * Per-thread chat capability token — sent as `Chat-Session-Token: <token>`
   * header. Issued by `POST /v1/businesses/{bizId}/chat/sessions` and stored in
   * the visitor's localStorage; authorizes send + poll on that ONE thread.
   * Distinct from bizToken (which only proves "legit storefront for biz X").
   */
  chatSessionToken?: string;
  /**
   * Client-side abort for THIS call, in ms (`fetchApiRequest` defaults to 15000).
   * Raise it only for calls whose server work legitimately outlives the
   * default — e.g. the chat start/send endpoints during a deploy-skew window
   * where an older API still runs the AI turn inside the request.
   */
  timeoutMs?: number;
}

/**
 * The embed client for our public, customer-facing `/v1/*` API
 * (the surface documented under "Public API Conventions" in the root
 * `AGENTS.md`). A thin adapter over `fetchApiRequest` from
 * `@tourtask/utils/shared/fetchApiRequest` (`apiRequest`'s contract over the
 * platform's fetch: no axios in the widget bundles, whose size has a budget)
 * that pre-binds the four things every v1 call needs:
 *
 *   1. The v1 API base URL (from `api/baseUrls`) — caller passes a
 *      path, not a full URL.
 *   2. The `Authorization: Bearer <bizToken>` header every per-business
 *      endpoint requires.
 *   3. `expect: 'result'` — every `/v1/*` endpoint returns a
 *      canonical `Result<T>` envelope, so the wrapper unwraps it.
 *   4. `withCredentials: false` — partner sites are cross-origin; we
 *      don't send cookies.
 *
 * Optionally also sets `Booking-Token` for booking-scoped endpoints.
 *
 * Returns `Result<T>` so error handling reads identically on both sides
 * of the client/server line.
 */
export function publicApiRequest<T>({
  path,
  method = 'GET',
  body,
  bizToken,
  bookingToken,
  turnstileToken,
  chatSessionToken,
  timeoutMs,
}: PublicApiRequestProps): Promise<Result<T>> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${bizToken}`,
  };
  if (bookingToken) {
    headers['Booking-Token'] = bookingToken;
  }
  if (turnstileToken) {
    headers['Turnstile-Token'] = turnstileToken;
  }
  if (chatSessionToken) {
    headers['Chat-Session-Token'] = chatSessionToken;
  }
  return fetchApiRequest<T>({
    method,
    url: `${apiV1BaseUrl}/${path}`,
    body,
    headers,
    expect: 'result',
    withCredentials: false,
    timeoutMs,
  });
}
